import { usePopUpMessage } from '@hb/react'
import {
  ConsentForm,
  DownloadConsentFormPdfArgs,
  DownloadConsentFormPdfResponse,
  USERS,
  WithId,
} from '@hb/shared'
import { useCallback, useState } from 'react'
import { uploadFile } from '../../backend/storage/utils'
import { downloadConsentFormPdf, uploadSignedConsentForm } from '../backend/consentForms/utils'

export const downloadConsentForm = async (consentFormId: string, patientId?: string) => {
  const args: DownloadConsentFormPdfArgs = { consentFormId }
  if (patientId) {
    args.patientId = patientId
  }
  const { data } = await downloadConsentFormPdf(args)
  const response = data as DownloadConsentFormPdfResponse | undefined
  if (response?.signedUrl && response?.fileName) {
    // Fetch the PDF as a blob to ensure it downloads instead of opening
    const pdfResponse = await fetch(response.signedUrl)
    const blob = await pdfResponse.blob()
    const blobUrl = window.URL.createObjectURL(blob)

    // Download file using blob URL
    const link = window.document.createElement('a')
    link.href = blobUrl
    link.download = response.fileName
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl)
    // showMessage({
    //   type: 'success',
    //   text: 'PDF downloaded successfully',
    // })
  } else {
    throw new Error('Invalid response from server')
  }
}

export const useConsentFormDownloadUpload = (document?: WithId<ConsentForm>) => {
  const { processResponse, showMessage } = usePopUpMessage()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dismissError = useCallback(() => setError(null), [])
  const handleDownload = useCallback(async () => {
    if (!document) {
      return processResponse({ error: 'No document selected' })
    }
    try {
      setIsDownloading(true)
      await downloadConsentForm(document.id, document.patientId)
      showMessage({
        type: 'success',
        text: 'PDF downloaded successfully',
      })
    } catch (err: any) {
      console.error(err)
      const errorMessage = err instanceof Error ? err.message : 'Error downloading PDF'
      setError('Error downloading PDF')
      return processResponse({
        error: errorMessage,
      })
    } finally {
      setIsDownloading(false)
    }
  }, [document, processResponse, showMessage])

  const handleUpload = useCallback(
    async (file: File) => {
      if (!document) {
        return processResponse({ error: 'No document selected' })
      }
      if (!file) {
        return processResponse({ error: 'Please select a file' })
      }
      if (file.type !== 'application/pdf') {
        return processResponse({ error: 'File must be a PDF' })
      }
      try {
        setIsUploading(true)
        const uid = document.toBeSignedByUser
        const now = new Date().toISOString()
        const storagePath = `${USERS}/${uid}/consent-forms-temp/${document.id}/signed-${now}.pdf`

        // Upload file to Firebase Storage
        await uploadFile(storagePath, file, () => {
          // Progress callback - can be used for UI feedback if needed
        })

        // Notify backend that file was uploaded
        await uploadSignedConsentForm({
          consentFormId: document.id,
          storagePath,
        })

        showMessage({
          type: 'success',
          text: 'Signed PDF uploaded successfully',
        })
        return { success: true }
      } catch (err: any) {
        console.error(err)
        const errorMessage = err instanceof Error ? err.message : 'Error uploading PDF'
        setError('Error uploading PDF')
        return processResponse({
          error: errorMessage,
        })
      } finally {
        setIsUploading(false)
      }
    },
    [document, processResponse, showMessage],
  )

  return {
    isDownloading,
    isUploading,
    error,
    handleDownload,
    handleUpload,
    dismissError,
  }
}
